import * as React from "react";
import { HelpCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/ui/table";
interface BenefitCategory {
  name: string;
  description: string;
  features: {
    name: string;
    description: string;
    tiers: {
      billing: string | boolean;
      workforce: string | boolean;
      utility: string | boolean;
    };
  }[];
}
const benefitsData: BenefitCategory[] = [{
  name: "Core Billing & Customer Management",
  description: "Essential billing operations and customer data management capabilities",
  features: [{
    name: "Consumer Information System",
    description: "Customer profiles, account management, and service tracking",
    tiers: {
      billing: "Basic profiles",
      workforce: "Basic + Advanced",
      utility: "Basic + Advanced + Enterprise"
    }
  }, {
    name: "Meter Data Management",
    description: "AMI/AMR data collection, validation, and historical storage",
    tiers: {
      billing: "Standard (2 years)",
      workforce: "Advanced (5 years)",
      utility: "Enterprise (Unlimited)"
    }
  }, {
    name: "Billing Engine",
    description: "Automated billing cycles and rate structure management",
    tiers: {
      billing: "Basic automation",
      workforce: "Basic automation",
      utility: "Advanced + AI optimization"
    }
  }, {
    name: "Payment Processing",
    description: "Payment gateway integrations and transaction management",
    tiers: {
      billing: "Basic gateways",
      workforce: "Basic gateways",
      utility: "Enterprise gateways + ACH"
    }
  }]
}, {
  name: "Customer Portal & Mobile Experience",
  description: "Self-service portals and mobile applications for customers",
  features: [{
    name: "Consumer Web Portal",
    description: "Online portal for bill viewing, payments, and account management",
    tiers: {
      billing: "Basic portal",
      workforce: "Advanced portal",
      utility: "White-label portal"
    }
  }, {
    name: "Consumer Mobile App",
    description: "Mobile application for customer self-service",
    tiers: {
      billing: "Basic app",
      workforce: "Enhanced app",
      utility: "White-label app"
    }
  }, {
    name: "Usage Analytics",
    description: "Detailed usage tracking and analytics for customers",
    tiers: {
      billing: "12 months history",
      workforce: "Hourly/daily analytics",
      utility: "AI-powered insights"
    }
  }]
}, {
  name: "Operations & Workforce Management",
  description: "Field operations, work orders, and asset management",
  features: [{
    name: "Service Order Management",
    description: "Work order creation, tracking, and automated dispatch",
    tiers: {
      billing: false,
      workforce: "Advanced system",
      utility: "Enterprise + AI routing"
    }
  }, {
    name: "Field Force Mobile App",
    description: "Mobile app for field workers with offline capabilities",
    tiers: {
      billing: false,
      workforce: "Standard app",
      utility: "Advanced + AR features"
    }
  }, {
    name: "Asset Management",
    description: "Equipment tracking, maintenance scheduling, and lifecycle management",
    tiers: {
      billing: false,
      workforce: "Basic tracking",
      utility: "AI-powered predictive"
    }
  }, {
    name: "GIS Integration",
    description: "Geographic mapping and spatial data management",
    tiers: {
      billing: false,
      workforce: "Basic mapping",
      utility: "Advanced Esri integration"
    }
  }]
}, {
  name: "Communication & Engagement",
  description: "Multi-channel communication and customer engagement tools",
  features: [{
    name: "Communication Hub",
    description: "Centralized platform for all customer communications",
    tiers: {
      billing: "Email delivery",
      workforce: "Multi-channel basic",
      utility: "Omnichannel + IVR"
    }
  }, {
    name: "Automated Notifications",
    description: "Trigger-based messaging and alerts",
    tiers: {
      billing: "Basic reminders",
      workforce: "Advanced triggers",
      utility: "AI-powered proactive"
    }
  }, {
    name: "Outage Communications",
    description: "Outage notifications and status updates",
    tiers: {
      billing: false,
      workforce: "Basic notifications",
      utility: "Advanced + Social media"
    }
  }]
}, {
  name: "Analytics & Business Intelligence",
  description: "Data analytics, reporting, and business intelligence capabilities",
  features: [{
    name: "Standard Reporting",
    description: "Pre-built reports for billing, payments, and operations",
    tiers: {
      billing: "Basic reports",
      workforce: "Operational KPIs",
      utility: "Executive dashboards"
    }
  }, {
    name: "Custom Report Builder",
    description: "Tools to create tailored reports and analytics",
    tiers: {
      billing: false,
      workforce: false,
      utility: "Advanced builder"
    }
  }, {
    name: "Predictive Analytics",
    description: "AI-powered forecasting and predictive insights",
    tiers: {
      billing: false,
      workforce: false,
      utility: "Full AI suite"
    }
  }, {
    name: "Revenue Analytics",
    description: "Advanced revenue tracking and optimization",
    tiers: {
      billing: "Basic tracking",
      workforce: "Advanced tracking",
      utility: "AI optimization"
    }
  }]
}, {
  name: "Integration & API Access",
  description: "System integrations and API capabilities",
  features: [{
    name: "Payment Processors",
    description: "Integration with payment gateways and processors",
    tiers: {
      billing: "Basic (Stripe, PayPal)",
      workforce: "Basic (Stripe, PayPal)",
      utility: "Enterprise gateways"
    }
  }, {
    name: "ERP Integration",
    description: "Enterprise resource planning system connectivity",
    tiers: {
      billing: "QuickBooks",
      workforce: "QuickBooks + advanced",
      utility: "SAP, Oracle, Dynamics"
    }
  }, {
    name: "Custom API Access",
    description: "RESTful APIs for custom integrations",
    tiers: {
      billing: false,
      workforce: "Limited access",
      utility: "Full API suite"
    }
  }, {
    name: "OEM Equipment Integration",
    description: "Direct integration with meter manufacturers and SCADA",
    tiers: {
      billing: false,
      workforce: false,
      utility: "Full integration"
    }
  }, {
    name: "GIS Integration",
    description: "Geographic Information System integration and mapping capabilities",
    tiers: {
      billing: false,
      workforce: "Basic GIS",
      utility: "Advanced Esri + ArcGIS"
    }
  }, {
    name: "General Integration Suite",
    description: "Productivity tools integration (Slack, Gmail, Google Meet, Calendar)",
    tiers: {
      billing: "Email only",
      workforce: "Slack + Calendar",
      utility: "Full suite + Teams"
    }
  }, {
    name: "Asset IoT Integration",
    description: "Internet of Things device connectivity and asset monitoring",
    tiers: {
      billing: false,
      workforce: "Basic IoT sensors",
      utility: "Advanced IoT + Edge computing"
    }
  }]
}, {
  name: "Enterprise Features",
  description: "Advanced enterprise capabilities and multi-tenant support",
  features: [{
    name: "Multi-Utility Support",
    description: "Manage multiple utilities from a single platform",
    tiers: {
      billing: false,
      workforce: false,
      utility: "Unlimited utilities"
    }
  }, {
    name: "Multi-Tenant Architecture",
    description: "Isolated tenant environments with shared resources",
    tiers: {
      billing: false,
      workforce: false,
      utility: "Full multi-tenancy"
    }
  }, {
    name: "White-Label Solutions",
    description: "Fully branded applications with custom domains",
    tiers: {
      billing: false,
      workforce: false,
      utility: "Complete branding"
    }
  }, {
    name: "Custom Development",
    description: "Custom feature development and integrations",
    tiers: {
      billing: false,
      workforce: "Limited",
      utility: "Full access"
    }
  }]
}, {
  name: "Security & Compliance",
  description: "Data security, access control, and regulatory compliance",
  features: [{
    name: "Data Encryption",
    description: "Encryption of data at rest and in transit",
    tiers: {
      billing: "Standard encryption",
      workforce: "Advanced encryption",
      utility: "Enterprise-grade"
    }
  }, {
    name: "Access Control",
    description: "Role-based permissions and authentication",
    tiers: {
      billing: "Basic roles",
      workforce: "Advanced RBAC",
      utility: "SSO + Multi-factor"
    }
  }, {
    name: "Compliance Tools",
    description: "Regulatory compliance and audit capabilities",
    tiers: {
      billing: "Basic compliance",
      workforce: "Standard compliance",
      utility: "GDPR/CCPA + Audit"
    }
  }]
}, {
  name: "Support & Professional Services",
  description: "Technical support levels and professional services",
  features: [{
    name: "Technical Support",
    description: "Available support channels and response times",
    tiers: {
      billing: "Email (business hours)",
      workforce: "Phone + Email",
      utility: "24/7 Premium support"
    }
  }, {
    name: "Response Time SLA",
    description: "Guaranteed response times for support requests",
    tiers: {
      billing: "24 hours",
      workforce: "8 hours priority",
      utility: "2 hours critical"
    }
  }, {
    name: "Account Management",
    description: "Dedicated account representative",
    tiers: {
      billing: false,
      workforce: false,
      utility: "Dedicated CSM"
    }
  }, {
    name: "Professional Services",
    description: "Implementation, training, and consulting services",
    tiers: {
      billing: "Basic training",
      workforce: "Enhanced training",
      utility: "Full service suite"
    }
  }]
}];
const BenefitsComparison = () => {
  return <Card className="mt-8">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-medium text-gray-800">Comprehensive Plan Features Comparison</CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-1">
              Detailed breakdown of features across Smart Billing, Smart Workforce, and Smart Utility plans
            </CardDescription>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-5 w-5 text-blue-600" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-sm">
                <p>This comprehensive comparison shows all features available across our three plan tiers. Higher tiers include all features from lower tiers plus additional enterprise capabilities.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[300px] font-medium text-gray-700">Feature Category & Details</TableHead>
                
                
                
              </TableRow>
            </TableHeader>
            <TableBody>
              {benefitsData.map(category => <React.Fragment key={category.name}>
                  <TableRow className="bg-gradient-to-r from-blue-50 to-blue-100">
                    <TableCell colSpan={4} className="font-semibold text-smart360-primary py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{category.name}</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="h-4 w-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-sm">
                              <p className="text-sm">{category.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                  </TableRow>
                  {category.features.map(feature => <TableRow key={feature.name} className="hover:bg-gray-50">
                      <TableCell className="font-medium pl-6">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{feature.name}</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <HelpCircle className="h-3 w-3 text-gray-400" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-sm">
                                <p className="text-xs">{feature.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {typeof feature.tiers.billing === 'boolean' ? feature.tiers.billing ? <span className="text-green-600 font-medium text-lg">✓</span> : <span className="text-gray-400 text-lg">—</span> : <span className="text-sm font-medium text-gray-700">{feature.tiers.billing}</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {typeof feature.tiers.workforce === 'boolean' ? feature.tiers.workforce ? <span className="text-green-600 font-medium text-lg">✓</span> : <span className="text-gray-400 text-lg">—</span> : <span className="text-sm font-medium text-gray-700">{feature.tiers.workforce}</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {typeof feature.tiers.utility === 'boolean' ? feature.tiers.utility ? <span className="text-green-600 font-medium text-lg">✓</span> : <span className="text-gray-400 text-lg">—</span> : <span className="text-sm font-medium text-gray-700">{feature.tiers.utility}</span>}
                      </TableCell>
                    </TableRow>)}
                </React.Fragment>)}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>;
};
export default BenefitsComparison;